import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { MainLayout } from '../layouts';
import { generateClient } from 'aws-amplify/api';
import { PhoneInput } from '../components/form';
import { formatPhoneNumberForAPI } from '../utils/formatters';

// Create a client for API calls
const client = generateClient();

// GraphQL mutation for updating email preferences
const updateEmailPreferencesMutation = `
  mutation UpdateEmailPreferences($input: EmailPreferencesInput!) {
    updateEmailPreferences(input: $input) {
      id
      emailPreferences {
        marketingConsent
        weeklyTipsSubscribed
      }
    }
  }
`;

const VerifyPhonePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get phone number from location state, localStorage, or use empty string
  const initialPhoneNumber = location.state?.phoneNumber || localStorage.getItem('tempPhoneNumber') || '';
  
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [dialCode, setDialCode] = useState('+61');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({
    phoneNumber: '',
    verificationCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [lastCodeSentTime, setLastCodeSentTime] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  
  // Add ref to prevent duplicate SMS resends
  const smsVerificationInProgress = useRef(false);
  
  const { verifyPhone, verifyEmailOtp, resendEmailOtp, resendVerificationCode, login, refreshAuthState } = useAuth();

  // Determine if this is an email OTP flow (set during registration for +1 numbers)
  const verificationMethod = localStorage.getItem('tempVerificationMethod') || 'sms';
  const isEmailMode = verificationMethod === 'email';
  const verificationEmail = localStorage.getItem('tempEmail') || '';
  
  // Cooldown period in seconds
  const COOLDOWN_PERIOD = 60;
  
  // Check if the cooldown period has elapsed
  const isCooldownActive = () => {
    if (!lastCodeSentTime) return false;
    const elapsedSeconds = Math.floor((Date.now() - lastCodeSentTime) / 1000);
    return elapsedSeconds < COOLDOWN_PERIOD;
  };
  
  // Update the cooldown timer
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (lastCodeSentTime && isCooldownActive()) {
      interval = setInterval(() => {
        const elapsedSeconds = Math.floor((Date.now() - lastCodeSentTime) / 1000);
        const remaining = Math.max(0, COOLDOWN_PERIOD - elapsedSeconds);
        setCooldownRemaining(remaining);
        
        if (remaining === 0) {
          clearInterval(interval!);
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [lastCodeSentTime]);
  
  // Validation function for verification code
  const validateVerificationCode = (value: string): string => {
    if (!value) return 'Verification code is required';
    if (!/^\d+$/.test(value)) return 'Verification code must contain only numbers';
    return '';
  };
  
  // Use the feature flag hook to check if verification should be skipped
  const skipVerification = useFeatureFlag('skipVerification');
  
  // If skipVerification is enabled, redirect to login page
  useEffect(() => {
    if (skipVerification) {
      navigate('/login', { 
        state: { 
          message: 'Verification is currently disabled. You can login directly.' 
        } 
      });
    }
  }, [skipVerification, navigate]);
  
  // Check if there's marketing consent in localStorage
  const [hasMarketingConsent, setHasMarketingConsent] = useState(false);
  
  useEffect(() => {
    const marketingConsent = localStorage.getItem('marketingConsent');
    if (marketingConsent === 'true') {
      setHasMarketingConsent(true);
    }
  }, []);
  
  // SMS verification is sent automatically by backend during registration
  // No need to track or manage code sending state on frontend
  
  // SMS verification is now sent automatically by the backend when registration succeeds
  // This page only handles verification, not sending the initial SMS
  // Users can still use the "Resend" button if needed
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!phoneNumber || !verificationCode) {
      setError('Please enter both phone number and verification code');
      return;
    }
    
    // Format phone number for API
    const formattedPhoneNumber = formatPhoneNumberForAPI(phoneNumber, dialCode);
    
    try {
      setError('');
      setIsSubmitting(true);

      let success: boolean;

      if (isEmailMode) {
        const marketingConsent = localStorage.getItem('marketingConsent') === 'true';
        const weeklyTipsSubscribed = localStorage.getItem('weeklyTipsSubscribed') === 'true';
        success = await verifyEmailOtp(verificationEmail, verificationCode, marketingConsent, weeklyTipsSubscribed);
      } else {
        success = await verifyPhone(formattedPhoneNumber, verificationCode);
      }

      if (success) {
        // Clear localStorage items that are no longer needed
        localStorage.removeItem('tempPhoneNumber');
        localStorage.removeItem('tempUserId');
        localStorage.removeItem('tempVerificationMethod');

        // Email OTP flow: auth state already set inside verifyEmailOtp — skipping
        // refreshAuthState here avoids the Cognito phone_number_verified check
        // redirecting back to this page for +1 users.
        if (!isEmailMode) {
          await refreshAuthState();
        }
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleResendCode = async () => {
    // Prevent duplicate resend requests
    if (smsVerificationInProgress.current || isResending) {
      console.log('SMS verification already in progress, ignoring resend request');
      return;
    }
    
    // Validate phone number
    if (!phoneNumber) {
      setError('Please enter your phone number');
      return;
    }
    
    // Check if cooldown is active
    if (isCooldownActive()) {
      setError(`Please wait ${cooldownRemaining} seconds before requesting another code`);
      return;
    }
    
    // Format phone number for API
    const formattedPhoneNumber = formatPhoneNumberForAPI(phoneNumber, dialCode);
    
    try {
      setError('');
      smsVerificationInProgress.current = true;
      setIsResending(true);
      
      const success = isEmailMode
        ? await resendEmailOtp(verificationEmail)
        : await resendVerificationCode(formattedPhoneNumber);
      
      if (success) {
        // Update last code sent time
        setLastCodeSentTime(Date.now());
        setCooldownRemaining(COOLDOWN_PERIOD);
        
        // Show success message
        setError('Verification code resent successfully');
        console.log('SMS verification code resent successfully');
      }
    } catch (err: any) {
      console.error('Error resending verification code:', err);
      setError(err.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setIsResending(false);
      smsVerificationInProgress.current = false;
    }
  };
  
  return (
    <MainLayout>
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">
          {isEmailMode ? 'Verify Your Email' : 'Verify Phone Number'}
        </h1>

        <div className="mb-6 text-center">
          <svg
            className="w-12 h-12 mx-auto mb-4 text-racing-blue"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-gray-600">
            {isEmailMode
              ? <>We sent a 6-digit code to <strong>{verificationEmail}</strong>. Enter it below to complete registration.</>
              : 'Please enter the verification code sent to your phone number to complete the registration process.'
            }
          </p>
        </div>
        
        {error && (
          <div className={`p-4 rounded-lg mb-6 ${
            error.includes('successfully')
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-racing-red text-white'
          }`}>
            {error.includes('successfully') && (
              <svg 
                className="w-5 h-5 inline-block mr-2 text-green-600" 
                fill="currentColor" 
                viewBox="0 0 20 20" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  fillRule="evenodd" 
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                  clipRule="evenodd"
                />
              </svg>
            )}
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Only show phone number input if we don't have it already */}
          {!initialPhoneNumber && (
            <div>
              <label htmlFor="phoneNumber" className="block text-gray-700 font-medium mb-2">
                Phone Number
              </label>
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                onCountryCodeChange={setDialCode}
                error={errors.phoneNumber}
                setError={(error) => setErrors(prev => ({ ...prev, phoneNumber: error }))}
                required
              />
            </div>
          )}
          
          {/* Show the phone number or email as text if we already have it */}
          {initialPhoneNumber && !isEmailMode && (
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">
                Phone Number
              </label>
              <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                {phoneNumber}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Verification code will be sent to this number
              </p>
            </div>
          )}

          {isEmailMode && (
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">
                Email Address
              </label>
              <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                {verificationEmail}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Verification code sent to this address
              </p>
            </div>
          )}
          
          <div>
            <label htmlFor="verificationCode" className="block text-gray-700 font-medium mb-2">
              Verification Code
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg 
                  className="h-5 w-5 text-gray-400" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 20 20" 
                  fill="currentColor" 
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="verificationCode"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                onBlur={() => {
                  const error = validateVerificationCode(verificationCode);
                  setErrors(prev => ({ ...prev, verificationCode: error }));
                }}
                className={`w-full pl-10 px-3 py-2 border ${errors.verificationCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-blue focus:border-racing-blue`}
                placeholder="123456"
                required
              />
            </div>
            {errors.verificationCode ? (
              <p className="text-red-500 text-xs mt-1">{errors.verificationCode}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                {isEmailMode ? 'Enter the 6-digit code sent to your email' : 'Enter the verification code sent to your phone'}
              </p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-racing-blue text-white py-3 px-4 rounded-lg font-medium hover:bg-racing-blue-dark transition flex items-center justify-center ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </>
            ) : (
              isEmailMode ? 'Verify Email' : 'Verify Phone Number'
            )}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          {isCooldownActive() ? (
            <div className="text-gray-500 inline-flex items-center">
              <svg className="mr-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              Resend available in {cooldownRemaining} seconds
            </div>
          ) : (
            <button
              onClick={handleResendCode}
              disabled={isResending}
              className={`text-racing-blue hover:underline inline-flex items-center ${
                isResending ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isResending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-racing-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resending...
                </>
              ) : (
                <>
                  <svg className="mr-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                  Resend Verification Code
                </>
              )}
            </button>
          )}
        </div>
        
        <div className="mt-6 text-center border-t border-gray-200 pt-6">
          <p className="text-gray-600">
            Already verified?{' '}
            <Link to="/login" className="text-racing-blue hover:underline font-medium">
              Login
            </Link>
          </p>
          <p className="text-gray-500 text-sm mt-3">
            Wrong details?{' '}
            <button
              onClick={() => {
                ['tempUserId', 'tempEmail', 'tempPhoneNumber', 'tempPassword',
                 'tempVerificationMethod', 'marketingConsent', 'weeklyTipsSubscribed'].forEach(k => localStorage.removeItem(k));
                navigate('/register');
              }}
              className="text-racing-blue hover:underline font-medium"
            >
              Start registration again
            </button>
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default VerifyPhonePage;
